import { type Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { type CalendarEvent } from '../../CalendarEvent'
import { useAllGoogleCalendarEvents } from '../../../../contexts/GoogleCalendar'
import { useDarkMode } from '../../../../providers/DarkModeProvider'
import {
  addAlphaToColor,
  darkenColor,
  desaturateColor,
} from '../../../../utils/color'
import { useAppleEvents } from '../../../../hooks/AppleCalDAV'
import { useMemo } from 'react'
import classNames from 'classnames'
import { useLocalStorage } from 'usehooks-ts'
import { useOutlookEvents } from '../../../../hooks/OutlookCalendar'

// Ensure we never call dayjs methods on raw values
const toDayjs = (d: unknown) =>
  dayjs.isDayjs(d) ? (d as Dayjs) : dayjs(d as any)

// Normalize and validate dates for a calendar event
const normalizeEventDates = (event: CalendarEvent) => {
  const startDate = toDayjs(event.startDate)
  const endDate = toDayjs(event.endDate)
  return { startDate, endDate }
}

// Reusable predicate: show all‑day event only on start day (avoid bleeding into next day)
const isAllDayEventVisibleFor = (
  event: CalendarEvent,
  selectedDay: Dayjs,
  sourceLabel: string
) => {
  const { startDate, endDate } = normalizeEventDates(event)

  if (!startDate.isValid() || !endDate.isValid()) {
    console.warn(`[AllDayEvents] Invalid ${sourceLabel} dates found:`, {
      eventId: event.id,
      originalStartDate: event.startDate,
      originalEndDate: event.endDate,
      convertedStartDate: startDate.toString(),
      convertedEndDate: endDate.toString(),
      startDateValid: startDate.isValid(),
      endDateValid: endDate.isValid(),
    })
    return false
  }

  const realEndDate = endDate.subtract(1, 'day')
  return (
    startDate.isSame(selectedDay, 'day') ||
    (startDate.isBefore(selectedDay, 'day') &&
      (realEndDate.isAfter(selectedDay, 'day') ||
        realEndDate.isSame(selectedDay, 'day')))
  )
}

// DRY filter for any provider
const filterAllDayEvents = (
  events: (CalendarEvent | undefined)[],
  selectedDay: Dayjs,
  sourceLabel: string
) =>
  events
    .filter((e): e is CalendarEvent => e !== undefined)
    .filter((event) => isAllDayEventVisibleFor(event, selectedDay, sourceLabel))

export function AllDayEvents({
  selectedDay,
  labelWidth,
}: {
  selectedDay: Dayjs
  labelWidth: number
}) {
  const isDarkMode = useDarkMode()
  const [hiddenCalendars] = useLocalStorage<string[]>('hiddenCalendars', [])

  const { data: googleEvents = [] } = useAllGoogleCalendarEvents(selectedDay)

  const appleResults = useAppleEvents(selectedDay)
  const appleEvents = useMemo(() => {
    const raw = appleResults.flatMap((r) => r.data ?? [])
    return filterAllDayEvents(raw, selectedDay, 'Apple')
  }, [appleResults, selectedDay])

  const outlookResults = useOutlookEvents(selectedDay)
  const outlookEvents = useMemo(() => {
    const raw = outlookResults.flatMap((r) => r.data ?? [])
    return filterAllDayEvents(raw, selectedDay, 'Outlook')
  }, [outlookResults, selectedDay])

  const allDayEvents: CalendarEvent[] = useMemo(() => {
    const filteredGoogleEvents = filterAllDayEvents(
      googleEvents,
      selectedDay,
      'Google'
    )

    return [...filteredGoogleEvents, ...appleEvents, ...outlookEvents].filter(
      (event) =>
        !hiddenCalendars.includes(event.calendarId ?? '') && event.allDay
    )
  }, [googleEvents, appleEvents, outlookEvents, hiddenCalendars, selectedDay])

  return (
    <div className='flex border-b-2'>
      <div
        className='flex flex-shrink-0 text-xs leading-tight text-gray-400'
        style={{ width: `${labelWidth.toString()}px` }}
      >
        <div className='w-full self-end pb-0.5 pr-2 text-right'>all-day</div>
      </div>
      <div className='grid flex-auto grid-cols-1 grid-rows-1 pt-0.5'>
        {allDayEvents.map((event) => {
          return (
            <div
              key={event.id}
              className={classNames(
                'mb-0.5 truncate rounded-md px-2 text-xs font-semibold',
                event.color ? '' : 'event-content-background-blue'
              )}
              style={{
                backgroundColor: addAlphaToColor(event.color, 0.22),
                color: event.color
                  ? darkenColor(
                      desaturateColor(event.color, 30),
                      isDarkMode ? -30 : 50
                    )
                  : 'gray',
              }}
            >
              {event.content}
            </div>
          )
        })}
      </div>
    </div>
  )
}
